import { z } from 'zod';
import { isoDateTime } from '../dokless/tracking-v2.schema.js';
// Per-step progress of a job (ADR 0057): the extension tells Plancia, step by
// step, where the read is, so the console's «prova in corso» log (S12) and the
// extension card (S18) follow it live. Best effort: the extension never waits
// on it and never fails a read because a progress post failed; the result
// (`POST /extension/jobs/{id}/result`) stays the only report that counts.
/** `started` → `ok` | `failed` | `skipped`; `waiting_human` when a wall stopped the step. */
export const JOB_STEP_STATUSES = ['started', 'ok', 'failed', 'skipped', 'waiting_human'];
export const jobStepStatusSchema = z.enum(JOB_STEP_STATUSES);
/** A thumbnail, not the capture: the extension sends a small JPEG (≈ 320 px wide). */
export const PROGRESS_SCREENSHOT_MAX_CHARS = 200_000;
/** Steps are indexed from 0; no recipe has this many. */
export const PROGRESS_MAX_STEP = 199;
/** Bounded challenge telemetry. Entered values and page/model text have no place in this contract. */
export const jobDiagnosticSchema = z.strictObject({
    phase: z.enum(['start', 'observation', 'decision', 'action', 'handoff', 'finish', 'error']),
    elapsed_ms: z.int().min(0).max(90_000).optional(),
    duration_ms: z.int().min(0).max(90_000).optional(),
    turn: z.int().min(0).max(8).optional(),
    frame_id: z.int().min(0).optional(),
    source: z.enum(['known_reference', 'model']).optional(),
    action: z.enum(['type', 'click', 'confirm', 'wait', 'done', 'give_up']).optional(),
    target_role: z.enum(['reference', 'code', 'search', 'other']).optional(),
    element: z.int().min(1).max(80).optional(),
    characters: z.int().min(0).max(200).optional(),
    ok: z.boolean().optional(),
    /** A short machine code, never an exception message or the model's explanation. */
    reason: z
        .string()
        .regex(/^[a-z][a-z0-9_]{0,63}$/)
        .optional(),
    fields: z
        .array(z.strictObject({
        role: z.enum(['reference', 'code', 'other']),
        empty: z.boolean(),
        chars: z.int().min(0).max(200),
        matches_request: z.boolean().optional(),
    }))
        .max(5)
        .optional(),
    busy: z.boolean().optional(),
    /** A blur event was observed; its source is separate from actual document focus. */
    field_blurred: z.boolean().optional(),
    outside_clicked: z.boolean().optional(),
    confirmation_source: z.enum(['native_blur', 'synthetic_confirmation', 'unobserved']).optional(),
    document_focused_before: z.boolean().optional(),
    document_focused_after: z.boolean().optional(),
    field_active_after: z.boolean().optional(),
    focus_requested: z.boolean().optional(),
    outcome: z.enum(['cleared', 'needs_human', 'stopped']).optional(),
    moves: z.int().min(0).max(8).optional(),
});
/** `POST /extension/jobs/{job_id}/progress`. */
export const jobProgressSchema = z.object({
    client_name: z.string().min(1).max(32),
    /** 0-based index of the recipe step (model mode: 0 open, 1 model, 2 capture). */
    step: z.int().min(0).max(PROGRESS_MAX_STEP),
    /** Steps in the run, when the extension knows it («passo 3/9»). */
    total: z
        .int()
        .min(1)
        .max(PROGRESS_MAX_STEP + 1)
        .nullable()
        .optional(),
    step_status: jobStepStatusSchema,
    /** The step's label as the extension shows it («Scrivi 6418827310»). */
    label: z.string().max(120).nullable().optional(),
    /** How long the step took (on `ok`/`failed`/`skipped`). */
    duration_ms: z.int().min(0).max(3_600_000).nullable().optional(),
    /** A small JPEG thumbnail of the page after the step, base64 without the data: prefix. */
    screenshot_jpeg_base64: z.string().max(PROGRESS_SCREENSHOT_MAX_CHARS).nullable().optional(),
    /** When it happened on the extension's clock. */
    at: isoDateTime,
    /** Structured field/action evidence without typed values; optional for older clients. */
    diagnostic: jobDiagnosticSchema.optional(),
});
export const jobProgressAcceptedSchema = z.object({ accepted: z.literal(true) });
//# sourceMappingURL=progress.schema.js.map